import {
  KineticLicenseFeature,
  KineticModule,
  useEffectivePrivilegesQuery,
  useEffectivePrivilegesUpdatedSubscription,
  useKineticUserQuery,
  useLicenseFeaturesQuery,
} from '@kinetic-ui/data-layer';
import { NullableUser, UnitType, UserContextData } from 'contexts/UserContext/UserContext';
import { groupBy, mapValues } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

const nullUser = {
  id: null,
  username: null,
  email: null,
  firstName: null,
  lastName: null,
  picture: null,
};

export const useUserContext = (): UserContextData => {
  const { data, loading, error } = useKineticUserQuery({ fetchPolicy: 'cache-first' });

  const [user, setUser] = useState<NullableUser>(nullUser);

  const unit: UnitType = useMemo(() => {
    var unitPreference = data?.kineticUser?.preferences?.find(
      (preference) => preference.key === 'UNIT'
    )?.value?.unit;
    if (!unitPreference) {
      return navigator.language === 'en-US' ? 'imperial' : 'metric';
    }
    return unitPreference;
  }, [data?.kineticUser.preferences]);

  const results = useEffectivePrivilegesQuery();
  useEffectivePrivilegesUpdatedSubscription({
    onSubscriptionData: () => results.refetch(),
  });

  const licensesResult = useLicenseFeaturesQuery();

  const enabledLicenses = useMemo(
    () =>
      licensesResult?.data?.licenseFeatures
        ?.filter((license) => license.enabled)
        .map((module) => module.feature),
    [licensesResult?.data?.licenseFeatures]
  );

  const enabledModules = useMemo(() => {
    const licenses = enabledLicenses || [];
    return [
      KineticModule.Integrations,
      KineticModule.Reports,
      KineticModule.System,
      KineticModule.Contacts,
      KineticModule.Emails,
      KineticModule.Locations,
      ...licenses,
    ];
  }, [enabledLicenses]);

  const privileges = useMemo(() => {
    if (!results.data || !enabledLicenses) {
      return {};
    }
    const mappedPrivileges = mapValues(
      groupBy(results.data.effectivePrivileges, 'kineticModule'),
      (list) => groupBy(list, 'privilegeType')
    );

    // Disable privileges if there is no license.
    Object.values(KineticLicenseFeature).forEach((feature) => {
      if (!enabledLicenses.includes(feature)) {
        mappedPrivileges[feature] = {};
      }
    });

    return mappedPrivileges;
  }, [results, enabledLicenses]);

  useEffect(() => {
    if (data?.kineticUser) {
      setUser(data.kineticUser);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      setUser(nullUser);
    }
  }, [error]);

  const contextValue = useMemo(
    () => ({
      ...user,
      privileges,
      unit,
      ready: !loading && !error && !!user && !!privileges,
      modules: enabledModules as KineticModule[],
    }),
    [user, privileges, loading, error, unit, enabledModules]
  );

  return contextValue;
};
